import { DefaultLanguage } from '../i18n/config';

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  /**
   * IANA time-zone name. When omitted, falls back to the browser's resolved
   * zone. Module code should pass `useUserLanguage().timeZone` so the
   * `User.PreferredTimeZone` override (once IDN ships) flows through.
   */
  timeZone?: string;
  /**
   * Locale (`en-US`, `es-MX`, `fr-CA`). When omitted, falls back to the
   * configured default. Module code should pass `useUserLanguage().language`
   * so the active SPA language drives the formatting.
   */
  locale?: string;
}

const defaultDateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

function resolveBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Formats an instant in the viewer's time zone per the active locale.
 *
 * Use this for any timestamp that represents a point in time (audit
 * `OccurredAt`, message `SentAt`, request `SubmittedAt`). Use
 * `formatBusinessDate` for calendar dates (billing periods, due dates) where
 * the value must not shift across viewers.
 */
export function formatDate(value: Date | string | number, opts: FormatDateOptions = {}): string {
  const { timeZone, locale, ...intlOpts } = opts;
  const date = value instanceof Date ? value : new Date(value);

  return new Intl.DateTimeFormat(locale ?? DefaultLanguage, {
    ...defaultDateOptions,
    ...intlOpts,
    timeZone: timeZone ?? resolveBrowserTimeZone(),
  }).format(date);
}
